/**
  * @class meleRow
  * @description
  *
  * # Purpose
  * Implements `<mele-row>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-row></mele-row>';
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-row',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  *
  */

import meleElement from 'meleElement'
const moment = require('moment')

const PROPERTIES = _.extend({}, {
  data: { description: 'internal data for the item', type: 'object', readonly: true, noattribute: true },
  section: { description: 'indicates if row is a section', type: 'boolean' },
  label: { description: 'content of the section row, will be localized', type: 'string' },
  selected: { description: 'indicates the button is selected', type: 'boolean' },
  visited: { description: 'indicates the row was visited', type: 'boolean' },
  bulkselected: { description: 'indicates the row is bulk-selected', type: 'boolean' },
}, meleElement._properties)

const TIMEFORMAT = {
  datetime: 'DD.MM.YYYY HH:mm',
  datetimems: 'DD.MM.YYYY HH:mm:ss',
  datetimeus: 'DD.MM.YYYY HH:mm:ss',
  date: 'DD.MM.YYYY',
  datems: 'DD.MM.YYYY',
  time: 'HH:mm',
  timems: 'HH:mm:ss',
  hm: 'HH:mm',
  hms: 'HH:mm:ss',
  datehm: 'DD.MM.YYYY HH:mm',
  datehms: 'DD.MM.YYYY HH:mm:ss',
  'date?hm': 'DD.MM.YYYY HH:mm',
  'date?hms': 'DD.MM.YYYY HH:mm:ss',
}

let td_template

export default class meleRow extends meleElement {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    if(!td_template) td_template = this.createElementFast('td')
    super.init()
    this.render()
  } // init

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * render the element using internal values and mele-table ancestor's definition
  */
  render(responsive) {
    const data = this.data || {}

    const table = this.closest('.mele-table')
    const columns = table ? table.columns : []
    const rowattributes = table ? table.rowattributes : []
    const hasBulk = table && Array.isArray(table.bulk) && table.bulk.length > 0

    // delete all atributes except pre-configured mele-row attributes
    const attr_to_delete=[]
    for(let i=0; i<this.attributes.length; i++) {
      const name = this.attributes[i].name
      if(!PROPERTIES[name]) { attr_to_delete.push(name) }
    }
    attr_to_delete.forEach(attr=>this.removeAttribute(attr))

    // setup attributes for properties configured to be visible in mele-row level
    rowattributes.forEach(attr=>{
      let val
      const d = data[attr];

      (!d || d == 'undefined') ? val = false :
        (d === 'false') ? val = false :
          (d === 'true') ? val = true :
            val = d

      val ? this.setAttribute(attr, val) : this.removeAttribute(attr) // important! attribute is taken from .data
    })

    const fragment = this.ownerDocument.createDocumentFragment()

    // handle bulk checkbox cell
    const startIdx = hasBulk ? 1 : 0
    if(hasBulk) {
      let bulkTd = this.children[0]
      if(!bulkTd || !bulkTd.classList.contains('bulk-checkbox')) {
        bulkTd = td_template.cloneNode()
        bulkTd.className = 'bulk-checkbox'
        const checkbox = this.ownerDocument.createElement('input')
        checkbox.type = 'checkbox'
        checkbox.checked = this.bulkselected
        checkbox.onclick = (e) => {
          e.stopPropagation()
          if(table) table.toggleBulkSelected(this.key, e.target.checked)
        }
        bulkTd.appendChild(checkbox)
        if(this.firstChild) {
          this.insertBefore(bulkTd, this.firstChild)
        } else {
          fragment.appendChild(bulkTd)
        }
      } else {
        // update checkbox state
        const checkbox = bulkTd.querySelector('input')
        if(checkbox) checkbox.checked = this.bulkselected
      }
    } else {
      // remove bulk checkbox if present
      const firstChild = this.children[0]
      if(firstChild && firstChild.classList.contains('bulk-checkbox')) {
        firstChild.remove()
      }
    }

    if(this.section) {
      // section row contains only one cell spanning across the row set with colspan attribute
      const totalCols = columns.length + startIdx
      while(this.children.length > startIdx + 1) this.lastChild.remove()
      let td = this.children[startIdx]
      if(!td) {
        td = td_template.cloneNode()
        fragment.appendChild(td)
      }
      td.setAttribute('colspan', columns.length)
      const label = String(this.translate(this.label)??'')
      if(String(td.innerHTML??'') !== label) td.innerHTML = label
    }else{
      // remove unnecessary columns (account for bulk checkbox)
      while(this.children.length > columns.length + startIdx) this.lastChild.remove()

      for(let i=0; i<columns.length; i++) {
        const col = columns[i]

        // translate dependent column
        let prefix = String(col.prefix??'')
        prefix = prefix.replace(/@{([^}]+)}/g, (m, key)=>{
          return data[key]
        })

        // calculate value
        let value = prefix + _.toString(col.key==='key' ? this.key : data[col.key]) // important! attribute is taken from .data (except "key")
        if(col.timeformat) {
          const format = TIMEFORMAT[col.timeformat] || TIMEFORMAT[String(col.timeformat).replace(/^format\./, '')] || col.timeformat
          value = parseInt(value)
          if(isNaN(value)) {
            value = ''
          }else {
            if(/us$/.test(col.timeformat)) {
              value = Math.floor(value/1000)
            }else if(!/ms$/.test(col.timeformat)) {
              value = _.inRange(value, 4102444800, -2840140800) ? value*1000 : value
            }
            value = moment(value)
            if(value.valueOf() && value.isValid()) {
              value = value.format(format)
              if(value === 'Invalid date') value = ''
            }else {
              value = ''
            }
          }
        }

        if(col.hourformat) {
          value = moment.utc(parseInt(value)).format(col.hourformat)
          if(value==='Invalid date') value=''
        }

        if(col.timefromstring) value = moment(value).format(col.timefromstring)

        if(prefix!=='' || col.localize) value = String(this.translate(value, true)??'')

        // handle icons and images
        const classname = col.icon ? 'icon' : col.img ? 'img' : ''

        if(col.wrapper) {
          value = col.wrapper.replace('@value', (value || '-'))
        }

        if(col.icon) {
          if(value) {
            const elIcon = this.getTemplate(value)
            value = elIcon ? elIcon.outerHTML : ''
          }
        }else if(col.img) {
          if(value) {
            value = '<img src="'+value+'" />'
          }
        }else{
          if(value==='') value = '-'
        }

        if(value !== '-') {
          if(col.before) value = col.before + ' ' + value
          if(col.after) value = value + ' ' + col.after
        }

        // get or create table cell (offset by startIdx for bulk checkbox)
        let td = this.children[i + startIdx]
        if(!td) {
          td = td_template.cloneNode()
          fragment.appendChild(td)
        }

        // fill the value
        if(td.innerHTML !== value) td.innerHTML = value

        // setup className
        if(td.className !== classname) td.className = classname

        // hide if desired
        if(col.hidden || col.temp_hidden) {
          if(td.style.display !== 'none') td.style.display = 'none'
        }else{
          if(td.style.display) td.style.removeProperty('display')
        }

        // responsive if desired
        const responsive_label = _.toString(col.label)
        if(responsive_label && responsive && td.getAttribute('responsive')!==responsive_label) {
          td.setAttribute('responsive', responsive_label)
        }else if(!responsive && td.hasAttribute('responsive')) {
          td.removeAttribute('responsive')
        }
      }
    }
    this.appendChild(fragment)
  } // render

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle event "click"
  */
  onclick(event) {
    if(this.hidden || this.disabled) return

    // calculate name of clicked column
    let clickedCol = ''
    let td = event.srcElement.closest('td,mele-row')
    if(td.tagName==='TD') {
      // skip if clicking on bulk checkbox cell
      if(td.classList.contains('bulk-checkbox')) return

      let idx = 0
      while((td = td.previousElementSibling) && td.tagName==='TD') idx++

      const table = this.closest('.mele-table')
      const hasBulk = table && Array.isArray(table.bulk) && table.bulk.length > 0

      // offset index by 1 if bulk checkbox is present
      if(hasBulk) idx--

      const columns = table ? (table.columns || []) : []
      const column = columns[idx]
      if(column) clickedCol = _.toString(column.key)
    }

    // find in which part of the item the click happened
    this.clicked(this)
    if(clickedCol!=='') this.clickedCell(this, event.srcElement, clickedCol)
    event.stopPropagation()
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * This could be overwritten to get information on item's click
  */
  clicked() {
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * This could be overwritten to get information on cell click
  */
  clickedCell(row, cell, column) {
    const holder = this.closest('.mele-table')
    if(holder && holder.eventCellClicked) {
      const row_values = row.data || {}
      const value = row_values[column]
      holder.eventCellClicked(holder, row.key, column, value)
    }
  }

  toText() {
    const row = []
    this.forAll('td', el=> {
      // skip bulk checkbox cell
      if(el.classList.contains('bulk-checkbox')) return
      row.push(el.innerText)
    })
    return row
  }

  visitedChanged() {}

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of bulkselected - update checkbox state
  */
  bulkselectedChanged(value) {
    const bulkTd = this.children[0]
    if(bulkTd && bulkTd.classList.contains('bulk-checkbox')) {
      const checkbox = bulkTd.querySelector('input')
      if(checkbox) checkbox.checked = value
    }
  } // bulkselectedChanged
} // class meleRow

meleRow._properties = PROPERTIES
meleRow.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-row', meleRow)
